import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import {
  Shield, AlertTriangle, Scale, MessageSquare, Heart, Plus, CheckCircle2,
  X, Clock, FileText, Send, ChevronDown, User, Lock, ClipboardCheck, Wrench, TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;
const RISK_LEVEL_COLORS = { low: 'text-[#A8E6CF] bg-[#A8E6CF]/10', medium: 'text-amber-400 bg-amber-500/10', high: 'text-red-400 bg-red-500/10', very_high: 'text-red-500 bg-red-500/20' };
const SG_TYPES = ['physical', 'emotional', 'financial', 'neglect', 'sexual', 'institutional', 'self_neglect', 'modern_slavery', 'domestic_abuse', 'discriminatory'];
const COMPLAINT_CATS = ['care_quality', 'staff_conduct', 'medication', 'communication', 'missed_visit', 'billing', 'safeguarding', 'data_privacy', 'other'];
const DOC_STATUSES = { initiated: 'text-amber-400 bg-amber-500/10', verbal_completed: 'text-blue-400 bg-blue-500/10', written_completed: 'text-[#8B5CF6] bg-[#8B5CF6]/10', complete: 'text-[#A8E6CF] bg-[#A8E6CF]/10' };

export default function GovernancePage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('risk');
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const isAdmin = ['agency_admin', 'ceo'].includes(user?.role);

  // Risk state
  const [riskTemplates, setRiskTemplates] = useState({});
  const [riskAssessments, setRiskAssessments] = useState([]);
  const [riskDialog, setRiskDialog] = useState(false);
  const [riskType, setRiskType] = useState('falls');
  const [riskScores, setRiskScores] = useState({});
  const [riskMitigation, setRiskMitigation] = useState('');

  // Safeguarding state
  const [sgConcerns, setSgConcerns] = useState([]);
  const [sgDialog, setSgDialog] = useState(false);
  const [sgForm, setSgForm] = useState({ concern_type: 'physical', description: '', immediate_risk: false, perpetrator_name: '', perpetrator_relationship: '', immediate_actions: '' });
  const [sgUpdateDialog, setSgUpdateDialog] = useState(null);
  const [sgUpdateForm, setSgUpdateForm] = useState({ content: '', status: '', la_referred: false, la_reference: '' });

  // Complaints state
  const [complaints, setComplaints] = useState([]);
  const [compDialog, setCompDialog] = useState(false);
  const [compForm, setCompForm] = useState({ complainant_name: '', category: 'care_quality', description: '', desired_outcome: '', method_received: 'verbal' });

  // Duty of Candour state
  const [docRecords, setDocRecords] = useState([]);
  const [docDialog, setDocDialog] = useState(false);
  const [docForm, setDocForm] = useState({ incident_id: '', qualifying_reason: '' });

  // IPC state
  const [ipcCategories, setIpcCategories] = useState([]);
  const [ipcAudits, setIpcAudits] = useState([]);
  const [ipcDialog, setIpcDialog] = useState(false);
  const [ipcResults, setIpcResults] = useState([]);
  const [ipcNotes, setIpcNotes] = useState('');
  const [ipcLocation, setIpcLocation] = useState('');
  const [ipcTrends, setIpcTrends] = useState([]);

  // Equipment state
  const [equipment, setEquipment] = useState([]);
  const [equipDialog, setEquipDialog] = useState(false);
  const [equipForm, setEquipForm] = useState({ device_type: 'wheelchair', device_name: '', serial_number: '', manufacturer: '', location: '', next_service_due: '', condition: 'good' });

  useEffect(() => { fetchClients(); fetchTemplates(); }, []);
  useEffect(() => { if (tab === 'risk' && selectedClient) fetchRisks(); }, [tab, selectedClient]);
  useEffect(() => { if (tab === 'safeguarding') fetchSG(); }, [tab]);
  useEffect(() => { if (tab === 'complaints') fetchComplaints(); }, [tab]);
  useEffect(() => { if (tab === 'doc') fetchDoC(); }, [tab]);
  useEffect(() => { if (tab === 'ipc') { fetchIpcCategories(); fetchIpcAudits(); fetchIpcTrends(); } }, [tab]);
  useEffect(() => { if (tab === 'equipment') fetchEquipment(); }, [tab]);

  const fetchClients = async () => { try { const { data } = await axios.get(`${API}/api/clinical/my-clients`, { withCredentials: true }); setClients(Array.isArray(data) ? data : []); } catch { setClients([]); } };
  const fetchTemplates = async () => { try { const { data } = await axios.get(`${API}/api/governance/risk-templates`, { withCredentials: true }); setRiskTemplates(data); } catch { /* ignore */ } };
  const fetchRisks = async () => { try { const { data } = await axios.get(`${API}/api/governance/risk-assessments/${selectedClient}`, { withCredentials: true }); setRiskAssessments(data); } catch { setRiskAssessments([]); } };
  const fetchSG = async () => { try { const { data } = await axios.get(`${API}/api/governance/safeguarding`, { withCredentials: true }); setSgConcerns(data); } catch { setSgConcerns([]); } };
  const fetchComplaints = async () => { try { const { data } = await axios.get(`${API}/api/governance/complaints`, { withCredentials: true }); setComplaints(data); } catch { setComplaints([]); } };
  const fetchDoC = async () => { try { const { data } = await axios.get(`${API}/api/governance/duty-of-candour`, { withCredentials: true }); setDocRecords(data); } catch { setDocRecords([]); } };
  const fetchIpcCategories = async () => { try { const { data } = await axios.get(`${API}/api/governance/ipc/categories`, { withCredentials: true }); setIpcCategories(data.categories || []); } catch { /* ignore */ } };
  const fetchIpcAudits = async () => { try { const { data } = await axios.get(`${API}/api/governance/ipc/audits`, { withCredentials: true }); setIpcAudits(data); } catch { setIpcAudits([]); } };
  const fetchIpcTrends = async () => { try { const { data } = await axios.get(`${API}/api/governance/ipc/trends`, { withCredentials: true }); setIpcTrends(data.trends || []); } catch { setIpcTrends([]); } };
  const fetchEquipment = async () => { try { const { data } = await axios.get(`${API}/api/governance/equipment`, { withCredentials: true }); setEquipment(data); } catch { setEquipment([]); } };

  // Risk handlers
  const saveRiskAssessment = async () => {
    try {
      await axios.post(`${API}/api/governance/risk-assessments`, { client_id: selectedClient, assessment_type: riskType, scores: riskScores, mitigation_plan: riskMitigation }, { withCredentials: true });
      toast.success('Risk assessment saved'); setRiskDialog(false); setRiskScores({}); setRiskMitigation(''); fetchRisks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // Safeguarding handlers
  const saveSG = async () => {
    try {
      await axios.post(`${API}/api/governance/safeguarding`, { client_id: selectedClient || null, ...sgForm }, { withCredentials: true });
      toast.success('Safeguarding concern filed — managers notified'); setSgDialog(false); fetchSG();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const saveSGUpdate = async () => {
    try {
      await axios.post(`${API}/api/governance/safeguarding/${sgUpdateDialog}/update`, sgUpdateForm, { withCredentials: true });
      toast.success('Safeguarding record updated'); setSgUpdateDialog(null); fetchSG();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // Complaint handlers
  const saveComplaint = async () => {
    try {
      await axios.post(`${API}/api/governance/complaints`, { client_id: selectedClient || null, ...compForm }, { withCredentials: true });
      toast.success('Complaint logged'); setCompDialog(false); fetchComplaints();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // DoC handlers
  const saveDoC = async () => {
    try {
      await axios.post(`${API}/api/governance/duty-of-candour`, { client_id: selectedClient || null, ...docForm }, { withCredentials: true });
      toast.success('Duty of Candour record created'); setDocDialog(false); fetchDoC();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const recordVerbal = async (id) => {
    const summary = prompt('Summary of verbal notification:');
    if (!summary) return;
    const givenTo = prompt('Notification given to (name):');
    try { await axios.put(`${API}/api/governance/duty-of-candour/${id}/verbal`, { summary, given_to: givenTo, apology_given: true }, { withCredentials: true }); toast.success('Verbal notification recorded'); fetchDoC(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const recordWritten = async (id) => {
    try { await axios.put(`${API}/api/governance/duty-of-candour/${id}/written`, { delivery_method: 'letter' }, { withCredentials: true }); toast.success('Written notification recorded'); fetchDoC(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const recordFollowup = async (id) => {
    const summary = prompt('Follow-up meeting summary:');
    if (!summary) return;
    try { await axios.put(`${API}/api/governance/duty-of-candour/${id}/followup`, { summary, outcome: 'discussed' }, { withCredentials: true }); toast.success('Follow-up recorded — DoC complete'); fetchDoC(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const template = riskTemplates[riskType];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="governance-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2"><Scale className="w-6 h-6 text-[#FF6F61]" />Governance</h1>
            <p className="text-sm text-muted-foreground mt-1">CQC Reg 12, 13, 16, 20 — Risk, Safeguarding, Complaints, Duty of Candour</p>
          </div>
          {(tab === 'risk') && (
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger className="bg-muted border-border text-foreground w-full sm:w-56" data-testid="gov-client-select"><SelectValue placeholder="Select client..." /></SelectTrigger>
              <SelectContent className="bg-background border-border">{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-muted rounded-lg overflow-x-auto">
          {[['risk', 'Risk Assessments'], ['safeguarding', 'Safeguarding'], ['complaints', 'Complaints'], ['doc', 'Duty of Candour'], ['ipc', 'IPC Audits'], ['equipment', 'Equipment']].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`flex-1 py-2 px-3 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${tab === k ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid={`tab-${k}`}>{l}</button>
          ))}
        </div>

        {/* ====== RISK ASSESSMENTS ====== */}
        {tab === 'risk' && (
          <div className="space-y-4" data-testid="risk-section">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Scored risk assessments with auto-calculated risk levels</p>
              {selectedClient && <Button size="sm" className="btn-primary" onClick={() => { setRiskDialog(true); setRiskScores({}); setRiskMitigation(''); }} data-testid="new-risk-btn"><Plus className="w-4 h-4 mr-1" />New Assessment</Button>}
            </div>
            {!selectedClient ? (
              <div className="text-center py-12"><User className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" /><p className="text-muted-foreground">Select a client first</p></div>
            ) : riskAssessments.length === 0 ? (
              <div className="text-center py-8"><Shield className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" /><p className="text-muted-foreground">No risk assessments recorded</p></div>
            ) : (
              <div className="space-y-2">
                {riskAssessments.map(ra => (
                  <Card key={ra.id} className="glass-card border-border" data-testid={`risk-${ra.id}`}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${RISK_LEVEL_COLORS[ra.risk_level] || 'bg-muted'}`}>
                        <AlertTriangle className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-foreground font-medium">{ra.tool_name}</p>
                        <p className="text-[10px] text-muted-foreground">Score: {ra.total_score} &bull; <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${RISK_LEVEL_COLORS[ra.risk_level]}`}>{ra.risk_level?.toUpperCase()}</span> &bull; {ra.assessment_date}</p>
                        {ra.mitigation_plan && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">Mitigation: {ra.mitigation_plan}</p>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ====== SAFEGUARDING ====== */}
        {tab === 'safeguarding' && (
          <div className="space-y-4" data-testid="safeguarding-section">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Safeguarding concerns — non-deletable, managers auto-notified</p>
              <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white" onClick={() => setSgDialog(true)} data-testid="raise-sg-btn"><AlertTriangle className="w-4 h-4 mr-1" />Raise Concern</Button>
            </div>
            {sgConcerns.length === 0 ? (
              <div className="text-center py-8"><Shield className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" /><p className="text-muted-foreground">No safeguarding concerns</p></div>
            ) : (
              <div className="space-y-3">
                {sgConcerns.map(sg => (
                  <Card key={sg.id} className={`glass-card border-border ${sg.immediate_risk ? 'border-red-500/40' : ''}`} data-testid={`sg-${sg.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Lock className="w-4 h-4 text-red-400" />
                            <p className="text-sm text-foreground font-medium">{sg.concern_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                            {sg.immediate_risk && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400">IMMEDIATE RISK</span>}
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${sg.status === 'closed' ? 'bg-muted text-muted-foreground' : 'bg-amber-500/10 text-amber-400'}`}>{sg.status}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{sg.description?.slice(0, 150)}{sg.description?.length > 150 ? '...' : ''}</p>
                          <p className="text-[10px] text-muted-foreground mt-1">Reported by {sg.reporter_name} ({sg.reporter_role}) on {sg.reported_at?.slice(0, 10)}</p>
                          {sg.local_authority_referral?.referred && <p className="text-[10px] text-[#00A6A0] mt-0.5">LA Referral: {sg.local_authority_referral.reference || 'Yes'}</p>}
                        </div>
                        {isAdmin && sg.status !== 'closed' && (
                          <Button size="sm" variant="outline" className="text-xs border-border text-muted-foreground" onClick={() => { setSgUpdateDialog(sg.id); setSgUpdateForm({ content: '', status: '', la_referred: false, la_reference: '' }); }} data-testid={`update-sg-${sg.id}`}>Update</Button>
                        )}
                      </div>
                      {sg.updates?.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-border/40 space-y-1">
                          {sg.updates.slice(-3).map((u, i) => (
                            <p key={i} className="text-[10px] text-muted-foreground">{u.timestamp?.slice(0, 16)} — {u.updated_by_name}: {u.content}</p>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ====== COMPLAINTS ====== */}
        {tab === 'complaints' && (
          <div className="space-y-4" data-testid="complaints-section">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Formal complaints with statutory 20-working-day deadline tracking</p>
              <Button size="sm" className="btn-primary" onClick={() => setCompDialog(true)} data-testid="log-complaint-btn"><Plus className="w-4 h-4 mr-1" />Log Complaint</Button>
            </div>
            {complaints.length === 0 ? (
              <div className="text-center py-8"><MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" /><p className="text-muted-foreground">No complaints logged</p></div>
            ) : (
              <div className="space-y-2">
                {complaints.map(c => (
                  <Card key={c.id} className={`glass-card border-border ${c.overdue ? 'border-red-500/40' : ''}`} data-testid={`complaint-${c.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm text-foreground font-medium">{c.category?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${c.status === 'closed' ? 'bg-muted text-muted-foreground' : c.status === 'responded' ? 'bg-[#A8E6CF]/10 text-[#A8E6CF]' : 'bg-amber-500/10 text-amber-400'}`}>{c.status}</span>
                            {c.overdue && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400">OVERDUE</span>}
                            {c.days_until_deadline != null && !c.overdue && c.status !== 'closed' && c.status !== 'responded' && (
                              <span className="text-[10px] text-muted-foreground">{c.days_until_deadline}d to deadline</span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{c.description}</p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">From: {c.complainant?.name || 'Anonymous'} &bull; Received: {c.date_received}</p>
                          {c.outcome && <p className="text-[10px] text-[#00A6A0] mt-0.5">Outcome: {c.outcome}</p>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ====== DUTY OF CANDOUR ====== */}
        {tab === 'doc' && isAdmin && (
          <div className="space-y-4" data-testid="doc-section">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Regulated 3-step process: verbal → written → follow-up</p>
              <Button size="sm" className="btn-primary" onClick={() => setDocDialog(true)} data-testid="new-doc-btn"><Plus className="w-4 h-4 mr-1" />New Record</Button>
            </div>
            {docRecords.length === 0 ? (
              <div className="text-center py-8"><Heart className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" /><p className="text-muted-foreground">No Duty of Candour records</p></div>
            ) : (
              <div className="space-y-3">
                {docRecords.map(d => (
                  <Card key={d.id} className="glass-card border-border" data-testid={`doc-${d.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Heart className="w-4 h-4 text-[#FF6F61]" />
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${DOC_STATUSES[d.status] || 'bg-muted text-muted-foreground'}`}>{d.status?.replace(/_/g, ' ').toUpperCase()}</span>
                        <span className="text-[10px] text-muted-foreground">Created {d.created_at?.slice(0, 10)}</span>
                      </div>
                      {d.qualifying_reason && <p className="text-xs text-muted-foreground mb-2">{d.qualifying_reason}</p>}
                      {/* 3-step progress */}
                      <div className="flex gap-2">
                        <div className={`flex-1 p-2 rounded-lg text-center text-[10px] ${d.verbal_notification?.completed ? 'bg-[#A8E6CF]/10 text-[#A8E6CF]' : 'bg-muted text-muted-foreground'}`}>
                          {d.verbal_notification?.completed ? <><CheckCircle2 className="w-3 h-3 mx-auto mb-0.5" />Verbal</> : (
                            <Button size="sm" variant="ghost" className="text-[10px] h-6 px-2 text-amber-400" onClick={() => recordVerbal(d.id)} data-testid={`verbal-${d.id}`}>Record Verbal</Button>
                          )}
                        </div>
                        <div className={`flex-1 p-2 rounded-lg text-center text-[10px] ${d.written_notification?.completed ? 'bg-[#A8E6CF]/10 text-[#A8E6CF]' : 'bg-muted text-muted-foreground'}`}>
                          {d.written_notification?.completed ? <><CheckCircle2 className="w-3 h-3 mx-auto mb-0.5" />Written</> : (
                            d.verbal_notification?.completed ? <Button size="sm" variant="ghost" className="text-[10px] h-6 px-2 text-amber-400" onClick={() => recordWritten(d.id)} data-testid={`written-${d.id}`}>Record Written</Button> : <span>Written</span>
                          )}
                        </div>
                        <div className={`flex-1 p-2 rounded-lg text-center text-[10px] ${d.follow_up?.completed ? 'bg-[#A8E6CF]/10 text-[#A8E6CF]' : 'bg-muted text-muted-foreground'}`}>
                          {d.follow_up?.completed ? <><CheckCircle2 className="w-3 h-3 mx-auto mb-0.5" />Follow-up</> : (
                            d.written_notification?.completed ? <Button size="sm" variant="ghost" className="text-[10px] h-6 px-2 text-amber-400" onClick={() => recordFollowup(d.id)} data-testid={`followup-${d.id}`}>Record Follow-up</Button> : <span>Follow-up</span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ====== IPC AUDITS (CQC Reg 12.5) ====== */}
        {tab === 'ipc' && (
          <div className="space-y-4" data-testid="ipc-section">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Infection Prevention & Control audit checklists</p>
              <Button size="sm" className="btn-primary" onClick={() => {
                const defaultResults = ipcCategories.map(cat => ({
                  category_id: cat.id, category_label: cat.label,
                  items: cat.items.map(item => ({ item, compliant: true, notes: '' }))
                }));
                setIpcResults(defaultResults);
                setIpcDialog(true);
              }} data-testid="new-ipc-btn"><ClipboardCheck className="w-4 h-4 mr-1" />New IPC Audit</Button>
            </div>

            {/* IPC Trends Chart */}
            {isAdmin && ipcTrends.length > 0 && (
              <Card className="glass-card border-border" data-testid="ipc-trends-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-4 h-4 text-[#00A6A0]" />
                    <span className="text-sm font-medium text-foreground">Monthly IPC Compliance Trends</span>
                  </div>
                  <div className="h-48 min-h-[192px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={ipcTrends.filter(t => t.avg_compliance_rate !== null)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="month_label" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} />
                        <YAxis domain={[0, 100]} tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, color: 'hsl(var(--foreground))' }} />
                        <ReferenceLine y={80} stroke="#A8E6CF" strokeDasharray="5 5" label={{ value: 'Target 80%', fill: '#A8E6CF', fontSize: 10 }} />
                        <Line type="monotone" dataKey="avg_compliance_rate" stroke="#00A6A0" strokeWidth={2} dot={{ fill: '#00A6A0', r: 4 }} name="Compliance %" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex gap-4 mt-2 text-[10px] text-muted-foreground">
                    {ipcTrends.filter(t => t.audit_count > 0).map(t => (
                      <span key={t.month}>{t.month_label}: {t.audit_count} audit{t.audit_count !== 1 ? 's' : ''}</span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {ipcAudits.length === 0 ? (
              <div className="text-center py-8"><ClipboardCheck className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" /><p className="text-muted-foreground">No IPC audits recorded</p></div>
            ) : (
              <div className="space-y-2">
                {ipcAudits.map(a => (
                  <Card key={a.id} className="glass-card border-border" data-testid={`ipc-${a.id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-foreground font-medium">{a.audit_date}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${a.compliance_rate >= 80 ? 'bg-[#A8E6CF]/10 text-[#A8E6CF]' : a.compliance_rate >= 50 ? 'bg-amber-500/10 text-amber-400' : 'bg-red-500/10 text-red-400'}`}>
                              {a.compliance_rate}%
                            </span>
                          </div>
                          <p className="text-[10px] text-muted-foreground">{a.auditor_name} &bull; {a.location || 'No location'} &bull; {a.compliant_items}/{a.total_items} compliant</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ====== EQUIPMENT REGISTER (CQC Reg 15.2) ====== */}
        {tab === 'equipment' && isAdmin && (
          <div className="space-y-4" data-testid="equipment-section">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Assistive equipment register with service tracking</p>
              <Button size="sm" className="btn-primary" onClick={() => { setEquipDialog(true); setEquipForm({ device_type: 'wheelchair', device_name: '', serial_number: '', manufacturer: '', location: '', next_service_due: '', condition: 'good' }); }} data-testid="new-equip-btn"><Wrench className="w-4 h-4 mr-1" />Register Equipment</Button>
            </div>

            {equipment.length === 0 ? (
              <div className="text-center py-8"><Wrench className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" /><p className="text-muted-foreground">No equipment registered</p></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {equipment.map(e => (
                  <Card key={e.id} className={`glass-card border-border ${e.service_overdue ? 'border-red-500/40' : ''}`} data-testid={`equip-${e.id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-foreground font-medium">{e.device_name || e.device_type?.replace(/_/g, ' ')}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${e.condition === 'good' ? 'bg-[#A8E6CF]/10 text-[#A8E6CF]' : e.condition === 'fair' ? 'bg-amber-500/10 text-amber-400' : 'bg-red-500/10 text-red-400'}`}>
                          {e.condition}
                        </span>
                      </div>
                      <p className="text-[10px] text-muted-foreground">{e.device_type?.replace(/_/g, ' ')} {e.serial_number ? `(S/N: ${e.serial_number})` : ''}</p>
                      {e.assigned_client_name && <p className="text-[10px] text-[#00A6A0] mt-1">Assigned to: {e.assigned_client_name}</p>}
                      {e.next_service_due && (
                        <p className={`text-[10px] mt-1 ${e.service_overdue ? 'text-red-400 font-medium' : 'text-muted-foreground'}`}>
                          Service due: {e.next_service_due} {e.service_overdue ? '(OVERDUE)' : ''}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* IPC Audit Dialog */}
      <Dialog open={ipcDialog} onOpenChange={setIpcDialog}>
        <DialogContent className="bg-background border-border max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-foreground">IPC Audit Checklist</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Input value={ipcLocation} onChange={e => setIpcLocation(e.target.value)} placeholder="Location / Client home" className="bg-muted border-border text-foreground" data-testid="ipc-location" />
            {ipcResults.map((cat, ci) => (
              <div key={cat.category_id}>
                <p className="text-xs font-medium text-foreground mb-2">{cat.category_label}</p>
                <div className="space-y-1">
                  {cat.items.map((item, ii) => (
                    <label key={ii} className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer" aria-label={item.item}>
                      <input type="checkbox" checked={item.compliant}
                        onChange={e => {
                          const updated = [...ipcResults];
                          updated[ci].items[ii].compliant = e.target.checked;
                          setIpcResults(updated);
                        }}
                        className="mt-0.5 flex-shrink-0" />
                      {item.item}
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <textarea value={ipcNotes} onChange={e => setIpcNotes(e.target.value)} placeholder="Overall notes..." className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[50px]" />
            <Button className="w-full btn-primary" data-testid="submit-ipc-btn"
              onClick={async () => {
                try {
                  await axios.post(`${API}/api/governance/ipc/audit`, { results: ipcResults, overall_notes: ipcNotes, location: ipcLocation }, { withCredentials: true });
                  toast.success('IPC audit recorded'); setIpcDialog(false); fetchIpcAudits();
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
              }}>
              Save IPC Audit
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Equipment Dialog */}
      <Dialog open={equipDialog} onOpenChange={setEquipDialog}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader><DialogTitle className="text-foreground">Register Equipment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <select value={equipForm.device_type} onChange={e => setEquipForm(f => ({ ...f, device_type: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm" data-testid="equip-type">
              {['wheelchair', 'hoist', 'hospital_bed', 'walking_frame', 'commode', 'pressure_mattress', 'bath_lift', 'stairlift', 'oxygen_concentrator', 'nebuliser', 'suction_unit', 'monitor', 'other'].map(t => (
                <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
              ))}
            </select>
            <Input value={equipForm.device_name} onChange={e => setEquipForm(f => ({ ...f, device_name: e.target.value }))} placeholder="Device name / model" className="bg-muted border-border text-foreground" data-testid="equip-name" />
            <div className="grid grid-cols-2 gap-2">
              <Input value={equipForm.serial_number} onChange={e => setEquipForm(f => ({ ...f, serial_number: e.target.value }))} placeholder="Serial number" className="bg-muted border-border text-foreground" />
              <Input value={equipForm.manufacturer} onChange={e => setEquipForm(f => ({ ...f, manufacturer: e.target.value }))} placeholder="Manufacturer" className="bg-muted border-border text-foreground" />
            </div>
            <Input value={equipForm.location} onChange={e => setEquipForm(f => ({ ...f, location: e.target.value }))} placeholder="Location" className="bg-muted border-border text-foreground" />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-muted-foreground">Next service due</label>
                <Input type="date" value={equipForm.next_service_due} onChange={e => setEquipForm(f => ({ ...f, next_service_due: e.target.value }))} className="bg-muted border-border text-foreground mt-1" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Condition</label>
                <select value={equipForm.condition} onChange={e => setEquipForm(f => ({ ...f, condition: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm mt-1">
                  <option value="good">Good</option><option value="fair">Fair</option><option value="poor">Poor</option>
                </select>
              </div>
            </div>
            <Button className="w-full btn-primary" data-testid="submit-equip-btn"
              onClick={async () => {
                try {
                  await axios.post(`${API}/api/governance/equipment`, equipForm, { withCredentials: true });
                  toast.success('Equipment registered'); setEquipDialog(false); fetchEquipment();
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
              }}>
              Register Equipment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={riskDialog} onOpenChange={setRiskDialog}>
        <DialogContent className="bg-background border-border max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-foreground">New Risk Assessment</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <select value={riskType} onChange={e => { setRiskType(e.target.value); setRiskScores({}); }} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm" data-testid="risk-type-select">
              {Object.entries(riskTemplates).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}
            </select>
            {template?.factors?.map(f => (
              <div key={f.id}>
                <label className="text-xs text-muted-foreground">{f.label}</label>
                <select value={riskScores[f.id] ?? ''} onChange={e => setRiskScores(s => ({ ...s, [f.id]: Number(e.target.value) }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm mt-1">
                  <option value="">Select...</option>
                  {f.options.map((o, i) => <option key={i} value={o.score}>{o.label} (score: {o.score})</option>)}
                </select>
              </div>
            ))}
            <div>
              <label className="text-xs text-muted-foreground">Mitigation Plan</label>
              <textarea value={riskMitigation} onChange={e => setRiskMitigation(e.target.value)} placeholder="Actions to reduce risk..." className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm mt-1 min-h-[60px]" />
            </div>
            <Button className="w-full btn-primary" onClick={saveRiskAssessment} data-testid="save-risk-btn">Save Assessment (Score: {Object.values(riskScores).reduce((a, b) => a + b, 0)})</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Safeguarding Dialog */}
      <Dialog open={sgDialog} onOpenChange={setSgDialog}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader><DialogTitle className="text-foreground text-red-400">Raise Safeguarding Concern</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <select value={sgForm.concern_type} onChange={e => setSgForm(f => ({ ...f, concern_type: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm" data-testid="sg-type-select">
              {SG_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
            </select>
            <textarea value={sgForm.description} onChange={e => setSgForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe the concern in detail..." className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[80px]" data-testid="sg-description" />
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={sgForm.immediate_risk} onChange={e => setSgForm(f => ({ ...f, immediate_risk: e.target.checked }))} />
              <label className="text-xs text-red-400 font-medium">Immediate risk to person</label>
            </div>
            <Input value={sgForm.perpetrator_name} onChange={e => setSgForm(f => ({ ...f, perpetrator_name: e.target.value }))} placeholder="Alleged perpetrator (if known)" className="bg-muted border-border text-foreground" />
            <textarea value={sgForm.immediate_actions} onChange={e => setSgForm(f => ({ ...f, immediate_actions: e.target.value }))} placeholder="Immediate actions taken..." className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[50px]" />
            <Button className="w-full bg-red-500 hover:bg-red-600 text-white" onClick={saveSG} data-testid="submit-sg-btn">File Safeguarding Concern</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Safeguarding Update Dialog */}
      <Dialog open={!!sgUpdateDialog} onOpenChange={o => !o && setSgUpdateDialog(null)}>
        <DialogContent className="bg-background border-border max-w-sm">
          <DialogHeader><DialogTitle className="text-foreground">Update Safeguarding Record</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <textarea value={sgUpdateForm.content} onChange={e => setSgUpdateForm(f => ({ ...f, content: e.target.value }))} placeholder="Update notes..." className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[60px]" />
            <select value={sgUpdateForm.status} onChange={e => setSgUpdateForm(f => ({ ...f, status: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm">
              <option value="">No status change</option><option value="under_investigation">Under Investigation</option><option value="referred">Referred</option><option value="closed">Closed</option>
            </select>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={sgUpdateForm.la_referred} onChange={e => setSgUpdateForm(f => ({ ...f, la_referred: e.target.checked }))} />
              <label className="text-xs text-muted-foreground">Referred to Local Authority (MASH)</label>
            </div>
            {sgUpdateForm.la_referred && <Input value={sgUpdateForm.la_reference} onChange={e => setSgUpdateForm(f => ({ ...f, la_reference: e.target.value }))} placeholder="LA reference number" className="bg-muted border-border text-foreground" />}
            <Button className="w-full btn-primary" onClick={saveSGUpdate}>Save Update</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Complaint Dialog */}
      <Dialog open={compDialog} onOpenChange={setCompDialog}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader><DialogTitle className="text-foreground">Log Complaint</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={compForm.complainant_name} onChange={e => setCompForm(f => ({ ...f, complainant_name: e.target.value }))} placeholder="Complainant name" className="bg-muted border-border text-foreground" data-testid="comp-name-input" />
            <select value={compForm.category} onChange={e => setCompForm(f => ({ ...f, category: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm" data-testid="comp-category-select">
              {COMPLAINT_CATS.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
            </select>
            <textarea value={compForm.description} onChange={e => setCompForm(f => ({ ...f, description: e.target.value }))} placeholder="Complaint description..." className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[80px]" data-testid="comp-description" />
            <Input value={compForm.desired_outcome} onChange={e => setCompForm(f => ({ ...f, desired_outcome: e.target.value }))} placeholder="Desired outcome" className="bg-muted border-border text-foreground" />
            <Button className="w-full btn-primary" onClick={saveComplaint} data-testid="submit-comp-btn">Log Complaint</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duty of Candour Dialog */}
      <Dialog open={docDialog} onOpenChange={setDocDialog}>
        <DialogContent className="bg-background border-border max-w-sm">
          <DialogHeader><DialogTitle className="text-foreground">New Duty of Candour Record</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={docForm.incident_id} onChange={e => setDocForm(f => ({ ...f, incident_id: e.target.value }))} placeholder="Related incident ID (optional)" className="bg-muted border-border text-foreground" />
            <textarea value={docForm.qualifying_reason} onChange={e => setDocForm(f => ({ ...f, qualifying_reason: e.target.value }))} placeholder="Why does this qualify for Duty of Candour? (notifiable safety incident)" className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[80px]" data-testid="doc-reason" />
            <Button className="w-full btn-primary" onClick={saveDoC} data-testid="submit-doc-btn">Create Record</Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
